import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Container } from 'reactstrap';

import {
  withTranslator,
  formatIsoDate,
  formatShortDate,
  scrollTo,
} from '@front-activerse/common-components';

import smoothscroll from 'smoothscroll-polyfill';

smoothscroll.polyfill();

const DEFAULT_ITEM_COUNT = 7;
const DEFAULT_ITEM_COUNT_DESKTOP = 19;
const ITEM_WIDTH_PX = 40;
const MAX_SCREEN_WIDTH = 1000;

const calcItemMargin = (screenWidth) => {
  const screenWidthToUse = screenWidth > MAX_SCREEN_WIDTH ? MAX_SCREEN_WIDTH : screenWidth;
  let itemCount = DEFAULT_ITEM_COUNT;

  if (screenWidthToUse > 575) {
    itemCount = DEFAULT_ITEM_COUNT_DESKTOP;
  }

  const divisor = itemCount + 1;
  const margin = (screenWidthToUse - itemCount * ITEM_WIDTH_PX) / divisor;

  if (margin < 0) {
    return -margin;
  } else {
    return margin;
  }
};

class Timeline extends Component {
  static propTypes = {
    t: PropTypes.func.isRequired,
    activeDate: PropTypes.instanceOf(Date).isRequired,
    dates: PropTypes.arrayOf(PropTypes.instanceOf(Date).isRequired).isRequired,
    children: PropTypes.func.isRequired,
    leftArrow: PropTypes.node,
    rightArrow: PropTypes.node,
  };

  state = {
    clickedDate: null,
    screenWidth: typeof window !== 'undefined' ? window.innerWidth : 0,
  };

  listRef = React.createRef();

  activeItemRef = React.createRef();

  scrollLeftRef = React.createRef();

  scrollRightRef = React.createRef();

  componentDidMount() {
    const handleResize = () => {
      this.setState({ screenWidth: window.innerWidth });
      this.scrollToActive({ behavior: 'jump' });
    };
    this.resizeHandler = handleResize;
    window.addEventListener('resize', this.resizeHandler);
    this.scrollToActive({ behavior: 'jump' });
  }

  componentWillUnmount() {
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.activeDate.valueOf() !== this.props.activeDate.valueOf()) {
      this.scrollToActive();
    }
  }

  formatDate = (date) => {
    const { t } = this.props;

    const weekday = date.getDay();
    const dayName = t(`weekday${weekday}-short`);
    const shortDate = formatShortDate(date);

    return (
      <React.Fragment>
        {dayName}
        <br />
        <span>{shortDate}</span>
      </React.Fragment>
    );
  };

  scrollToActive(options) {
    const activeItem = this.activeItemRef.current;
    if (!activeItem) {
      return;
    }
    const behavior = options?.behavior === 'jump' ? 'auto' : 'smooth';
    activeItem.scrollIntoView({
      behavior,
      block: 'nearest',
      inline: 'center',
    });
  }

  scrollHorizontally = (sgn) => {
    const list = this.listRef.current;
    const scrollLeft = this.scrollLeftRef.current;
    const scrollRight = this.scrollRightRef.current;
    const left =
      list.scrollLeft + sgn * (list.offsetWidth - scrollLeft.offsetWidth - scrollRight.offsetWidth);
    scrollTo(list, { top: 0, left });
  };

  scrollLeft = () => {
    this.scrollHorizontally(-1);
  };

  scrollRight = () => {
    this.scrollHorizontally(1);
  };

  renderScrollLeft() {
    return (
      <button
        className="timeline-arrow left"
        onClick={() => this.scrollLeft()}
        type="button"
        ref={this.scrollLeftRef}
        data-test="left"
      >
        <span className="SVGInline">{this.props.leftArrow}</span>
      </button>
    );
  }

  renderScrollRight() {
    return (
      <button
        className="timeline-arrow right"
        onClick={() => this.scrollRight()}
        type="button"
        ref={this.scrollRightRef}
        data-test="right"
      >
        <span className="SVGInline">{this.props.rightArrow}</span>
      </button>
    );
  }

  renderItem = (date, itemMargin, isLast) => {
    const { activeDate, children: renderDate } = this.props;

    const { clickedDate } = this.state;

    const active = date.valueOf() === activeDate.valueOf();

    const clicked = clickedDate ? date.valueOf() === clickedDate.valueOf : false;

    const linkClassNames = ['nav-link'];
    if (active) {
      linkClassNames.push('active');
    }
    if (clicked) {
      linkClassNames.push('clicked');
    }

    return (
      <li
        className="nav-item"
        key={date}
        ref={active ? this.activeItemRef : null}
        data-test={active ? 'active' : false}
        style={{
          marginLeft: itemMargin,
          ...(isLast && { marginRight: itemMargin }),
        }}
      >
        {renderDate({
          date,
          active,
          clicked,
          isoDate: formatIsoDate(date),
          formattedDate: this.formatDate(date),
          linkClassName: linkClassNames.join(' '),
          formatDate: this.formatDate,
        })}
      </li>
    );
  };

  render() {
    const { dates } = this.props;
    const { screenWidth } = this.state;
    const itemMargin = calcItemMargin(screenWidth);

    return (
      <section className="timeline">
        <Container>
          <nav>
            {this.renderScrollLeft()}
            {this.renderScrollRight()}
            <ul ref={this.listRef} data-test="list">
              {dates.map((date, index) =>
                this.renderItem(date, itemMargin, index === dates.length - 1)
              )}
            </ul>
          </nav>
        </Container>
      </section>
    );
  }
}

export { Timeline };
export default withTranslator(Timeline);
